"use client";

import { Button } from "@/components/atoms/Button";
import ExternalLinkIcon from "@/assets/ExternalLinkIcon";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useRef, useState } from "react";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { useDispatch } from "react-redux";

interface CareerProspectMetadata {
  id: string;
  icon: string;
  alt_icon?: string;
  text: string;
  priority: number;
  hover_icon: string;
  description: string;
  title_text_color: string;
  description_text_color: string;
}

interface CareerProspectsData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title: string;
  title_text_color?: string;
  sub_title: string;
  sub_title_text_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_color?: string;
  cta_icon?: string;
  background_color?: string;
  variant?: string;
  metadata: CareerProspectMetadata[];
}

interface CareerProspects1Props {
  data: {
    data: CareerProspectsData;
  };
}

function CareerProspects1({ data }: any) {
  const dispatch = useDispatch();
  const isMobileView = useIsMobile(768);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef<HTMLElement>(null);


  const {
    title = "Career Prospects",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    cta_text = "Load more",
    cta_text_color = "#F5F5F5",
    cta_background_color = "#F74958",
    cta_icon = "",
    cta_icon_alt = "Read more",
    cta_variant = "variant_1",
    design_variant = "variant_1",
    background_color = "#F8F9FA",
    show_apply_now = false,
    apply_now_text = "Apply Now",
    apply_now_text_color = "#FFFFFF",
    apply_now_background_color = "#9F1C34",
    metadata = [],
  } = data?.data || {};

  // Design variant — variant_1 = current design (unchanged), variant_2 = Figma
  // tokens (fonts + spacing). variant_1 reuses the exact existing classes so
  // nothing changes for existing instances.
  const isV2 = design_variant === "variant_2";
  // Figma uses Poppins; only applied for variant_2.
  const v2Font = isV2 ? { fontFamily: "var(--font-poppins)" } : {};

  // Section padding
  const sectionPadClass = isV2
    ? "px-[24px] py-[40px] sm:px-[40px] sm:py-[48px] md:px-[54px] md:py-[54px] lg:px-[70px] lg:py-[60px] xl:px-[70px] xl:py-[60px]"
    : "px-5 py-8 xs:px-6 sm:p-[42px] md:px-[54px] md:py-[42px] lg:px-16 lg:py-[42px] xl:px-[120px] xl:py-[42px]";
  // Section gap (header / cards / cta)
  const containerGapClass = isV2
    ? "gap-[24px] sm:gap-[30px] md:gap-[36px] lg:gap-[40px]"
    : "gap-6 xs:gap-6 sm:gap-6";
  // Heading font
  const headingClass = isV2
    ? "font-semibold leading-[1.25] text-[28px] sm:text-[31px] md:text-[33px] lg:text-[36px] tracking-[-0.42px] lg:tracking-[-0.54px]"
    : "text-heading-6-semibold sm:text-new-heading-34-semibold";
  // Cards grid + column gap
  const cardsGridClass = isV2
    ? "grid w-full grid-cols-1 gap-[32px] sm:grid-cols-3 sm:gap-[32px] md:gap-[36px] lg:gap-[40px]"
    : "grid w-full grid-cols-1 gap-5 sm:grid-cols-3 sm:gap-5 xl:gap-10";
  // Card padding
  const cardClass = isV2
    ? "group flex flex-col items-center w-full cursor-pointer transition-colors duration-300 gap-2 rounded-[18px] bg-transparent shadow-none p-0 sm:p-[8px] md:p-[16px] lg:p-[24px] sm:rounded-[18px] sm:bg-transparent sm:justify-center"
    : "group flex flex-col items-center w-full cursor-pointer transition-colors duration-300 gap-2 rounded-[18px] bg-transparent px-4 py-5 shadow-none xs:px-4 xs:py-5 sm:rounded-[18px] sm:bg-transparent sm:justify-center sm:px-4 sm:py-5 md:px-6 md:py-5 xl:px-8 xl:py-5";
  // Icon circle size
  const iconBoxClass = isV2
    ? "relative size-[54px] sm:size-[58px] md:size-[62px] lg:size-[68px]"
    : "relative size-[68px]";
  // Card title font
  const cardTitleClass = isV2
    ? "w-full line-clamp-2 font-semibold leading-[1.5] text-[16px] sm:text-[17px] lg:text-[18px]"
    : "text-label-4-semibold w-full line-clamp-2";
  // Card description font
  const cardDescClass = isV2
    ? "w-full text-center line-clamp-3 font-normal leading-[1.55] text-[14px] md:text-[15px] tracking-[0.07px] lg:tracking-[0.075px]"
    : "text-body-4-regular w-full text-center line-clamp-3 sm:text-body-4-regular md:text-body-3-regular lg:text-body-2-regular";
  // Apply Now width — Figma mobile is full-width (variant_2 only)
  const applyNowWidthClass = isV2 ? "w-full sm:w-fit" : "w-fit";

  // Sort metadata by priority
  const sortedMetadata = [...metadata].sort(
    (a, b) => (a.priority || 0) - (b.priority || 0),
  );

  const mobileInitialCount = 3;
  const desktopInitialCount = 6;
  const initialVisibleCount =
    isMobileView === true ? mobileInitialCount : desktopInitialCount;

  const visibleItems = showAll
    ? sortedMetadata
    : sortedMetadata.slice(0, initialVisibleCount);

  const hasMore = !showAll && sortedMetadata.length > initialVisibleCount;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "career_prospects_section");
  //   }
  // }, []);

  return (
    <section
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center w-full",
        sectionPadClass,
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex flex-col items-center w-full",
          containerGapClass,
          "max-w-full sm:max-w-[996px] md:max-w-[1122px] lg:max-w-[1302px] xl:max-w-[1510px]",
        )}
      >
        {/* Mobile: H6 24 + B3 14; sm+: NH-34 + B1-18 */}
        <div
          className={cn(
            "flex flex-col items-center text-center w-full",
            "gap-2 xs:gap-[10px] sm:gap-[10px]",
          )}
        >
          {title && (
            <h2
              className={cn(headingClass)}
              style={{ color: title_text_color || "#101112", ...v2Font }}
            >
              {title}
            </h2>
          )}

          {sub_title && (
            <p
              className={cn(
                "text-body-3-regular w-full max-w-4xl mx-auto",
                "sm:text-body-1-regular sm:max-w-[560px]",
                "type-body-sm",
              )}
              style={{ color: sub_title_text_color || "#101112" }}
            >
              {sub_title}
            </p>
          )}
        </div>

        {/* Mobile: 1 col, gap-20; sm+: 3 col grid */}
        <div className={cn(cardsGridClass)}>
          {visibleItems.map((item) => {
            const isHovered = hoveredCard === item.id;
            const hoverIcon = item.hover_icon?.trim();
            const iconSrc = isHovered && hoverIcon ? hoverIcon : item.icon;

            return (
              <div
                key={item.id}
                className={cn(cardClass)}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className={cn(
                    "flex flex-col items-center justify-center w-full text-center",
                    "gap-2 xs:gap-[10px] sm:gap-[10px]",
                  )}
                >
                  {item.icon && (
                    <div
                      className={cn(
                        "flex items-center justify-center rounded-full overflow-hidden shrink-0 transition-colors duration-300",
                        isHovered && "bg-[#F74958]",
                      )}
                    >
                      <div className={cn(iconBoxClass)}>
                        <Image
                          src={iconSrc}
                          alt={item.alt_icon || item.text || "Career prospect"}
                          fill
                          className="object-contain transition-all duration-300"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex w-full justify-center min-h-[20px]">
                    <p
                      className={cn(cardTitleClass)}
                      style={{
                        color: item.title_text_color || "#222222",
                        ...v2Font,
                      }}
                    >
                      {item.text}
                    </p>
                  </div>
                </div>

                {/* lg [1430]: B2-16, min-h 72px per Figma */}
                <div className="flex w-full justify-center lg:min-h-[72px]">
                  <p
                    className={cn(cardDescClass)}
                    style={{
                      color: item.description_text_color || "#222222",
                      ...v2Font,
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load more / View More — Variant 1: filled button on all screens.
            Variant 2: underlined text link on mobile, filled button on desktop. */}
        {hasMore && cta_text && (
          <>
            {/* Variant 2 — underlined text link (all breakpoints, no button chrome), Figma 518:3841 */}
            {cta_variant === "variant_2" && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className={cn(
                  "flex w-fit cursor-pointer items-center justify-center",
                  "text-[16px] font-semibold leading-[1.4]",
                  "underline [text-underline-position:from-font]",
                )}
                style={{
                  fontFamily: "var(--font-poppins)",
                  color: data?.data?.cta_text_color || "#000000",
                  textDecorationColor: data?.data?.cta_text_color || "#000000",
                }}
              >
                {cta_text}
              </button>
            )}

            {/* Variant 1 only — filled button. Variant 2 uses the underlined link above. */}
            <Button
              className={cn(
                "w-fit cursor-pointer flex items-center justify-center",
                "min-h-[53px] h-[53px] rounded-[10px] px-6 py-2 gap-[10px]",
                "text-heading-6-semibold",
                "shadow-[0px_4.613px_3.344px_0px_rgba(202,150,155,0.34)]",
                cta_variant === "variant_2" && "hidden",
              )}
              style={{
                backgroundColor: cta_background_color || "#F74958",
                color: cta_text_color || "#F5F5F5",
              }}
              onClick={() => setShowAll(true)}
            >
              {cta_text}
              {cta_icon && (
                <div className="relative size-9 shrink-0">
                  <Image src={cta_icon} alt={cta_icon_alt || "CTA"} fill />
                </div>
              )}
            </Button>
          </>
        )}

        {/* Apply Now — optional CTA, toggled via show_apply_now.
            Coerce to a real boolean: MySQL returns the flag as 0/1, and `{0 && …}`
            would render a literal "0". */}
        {Boolean(show_apply_now) && apply_now_text && (
          <button
            type="button"
            className={cn(
              applyNowWidthClass,
              "cursor-pointer flex items-center justify-center gap-[10px]",
              "rounded-[8px] px-6 py-4",
              "text-body-2-semibold whitespace-nowrap",
              "drop-shadow-[0px_10px_10px_rgba(202,125,139,0.6)]",
              "transition-opacity duration-200 hover:opacity-90",
            )}
            style={{
              backgroundColor: apply_now_background_color || "#9F1C34",
              color: apply_now_text_color || "#FFFFFF",
            }}
            onClick={() =>
              dispatch(
                handleOpenLeaddrop({
                  variant: LEAD_DROP_VARIANTS.REGISTER,
                }),
              )
            }
          >
            {apply_now_text}
            <ExternalLinkIcon className="size-6 shrink-0" />
          </button>
        )}
      </div>
    </section>
  );
}

export default CareerProspects1;
